<template>
  <v-app id="app">
    <component v-bind:is="dynamicLayout"></component>
  </v-app>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

import { isEmpty } from 'ramda'

const AppLayout = () => import('@/layouts/AppLayout')
const FullPageLayout = () => import('@/layouts/FullPageLayout')

export default {
  provide () {
    return {
      flash: this.$flash
    }
  },
  data: function () {
    return {
    }
  },
  components: {
    'app-layout': AppLayout,
    'full-page-layout': FullPageLayout
  },
  watch: {
    /**
     * This Watcher handles associating Dealer.
     *   * Sets Dealer if currentDealer is known
     *   * Sets currentDealer if dealer is changed
     *   * Sets to default dealer if neither of above...
     *  See also beforeMount/loadAppSettings
     */
    dealer: {
      immediate: false,
      handler (newDealer, oldDealer) {
        // case when new - set to default dealer...
        this.$logger.trace('DEALER: Executing Dealer Select Logic...')
        if (!isEmpty(this.currentDealer || {}) && isEmpty(newDealer || {})) {
          this.$logger.trace('DEALER: Have existing current dealer. No newDealer')
          this.saveDealer(this.currentDealer)
        } else if (!isEmpty(newDealer || {})) {
          if ((this.currentDealer || {}).id !== newDealer.id) {
            this.$logger.trace('DEALER: Setting current dealer to ', newDealer.name)
            this.currentDealer = newDealer
          }
        } else {
          this.$logger.trace('DEALER: No State. Setting to default')
          this.$store.dispatch('DealerStore/fetchDealers')
            .then(dealers => {
              this.$logger.trace('DEALER: No Dealers selected. Setting default to default account dealer')
              const dlr = dealers.find(d => d.is_account_dealer) // the main dealer...
              this.saveDealer(dlr) // set on the dealer store - next watch change will set current...
            })
            .catch(error => {
              if (error.statusCode !== 401) {
                this.$logger.error('APP: Error fetching dealers', error.message)
                this.$flash({
                  showClose: true,
                  message: 'Error fetching dealers',
                  type: 'error',
                  duration: 60
                })
              }
            })
        }
      }
    }
  },
  computed: {
    ...mapState('DealerStore', ['dealer']),
    ...mapState('ApplicationStore', ['showFullPageView']),
    currentDealer: {
      get: function () {
        return this.$store.state.ApplicationStore.currentDealer
      },
      set: function (dealer) {
        return this.$store.commit('ApplicationStore/setCurrentDealer', dealer)
      }
    },
    dynamicLayout () {
      if (this.showFullPageView) {
        return 'full-page-layout'
      } else {
        return 'app-layout'
      }
    }
  },
  methods: {
    ...mapMutations('DealerStore', ['saveDealer']),
    /**
     * This works in conjunction with above Dealer watch
     * This file is the main Vue Application component
     * On first load (or reload) look at the local app settings
     * so we have a context (currentDealer, etc) in the apps.
     * The Dealer watch code will handle (at least of the dealer case)
     * where this is a first run and no previous context set...
     */
    loadAppSettings: async function () {
      this.$logger.trace('Loading Current Settings')
      this.$store.dispatch('ApplicationStore/loadLocalAppSettings')
        .then(appSettings => {
        // possibly make setting this conditional on dealerScopedView=true
          this.saveDealer(this.currrentDealer)
        }).catch(error => {
          this.$logger.error('Error fetching app settings', error.message)
          this.$flash({
            showClose: true,
            message: 'Error loading app settings! Setting may be out of sync.',
            type: 'error',
            duration: 60
          })
        })
    }
  },
  mounted () {
    this.$store.dispatch('DealerStore/fetchDealers')
      .then(dealers => {
        this.$logger.trace('APP: Dealers Loaded...')
        this.loadAppSettings()
      })
      .catch(error => {
        if (error.status !== 401) {
          this.$logger.error('APP: Error fetching dealers', error.message)
          this.$flash({
            showClose: true,
            message: 'Error fetching dealers',
            type: 'error',
            duration: 60
          })
        }
      })
  },
  beforeMount: async function () {
    this.$logger.trace('App Before Mount')
    await this.loadAppSettings()
  }
}
</script>

<style>
  .light-header-color {
    color: rgba(0, 0, 0, 0.54);
  }
  .custom-loader {
    animation: loader 1s infinite;
    display: flex;
  }
  @-moz-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-webkit-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-o-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
</style>
